import React, { useMemo, useState, useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";
import {
  FiPlus,
  FiTrash2,
  FiGlobe,
  FiChevronDown,
  FiChevronUp,
} from "react-icons/fi";
import { useDispatch, useSelector } from "react-redux";
import { Button } from "@mui/material";
import CreatableSelect from "react-select/creatable";
import {
  addQuestion,
  updateQuestion,
  fetchQuestionById,
  translateQuestion,
  removeTranslatedQuestion,
  clearSelectedQuestion,
  fetchQuestionTags,
} from "../../../store/reducers/questionsSectionSlice";
import { toast } from "react-toastify";
import { fetchCombinedCode } from "../../../store/reducers/countryLanguageSlice";
import FormPageHeader from "../components/FormPageHeader";
import { MdDeleteOutline } from "react-icons/md";
import {
  DEFAULT_COUNTRY_LANGUAGE_CODE,
  findDefaultCountryLanguageOption,
  isDefaultCountryLanguageSection,
} from "../../../utils/defaultCountryLanguage";


const THEME_COLOR = "#f85c63";

const parseCombinedCode = (combined_code = "", id = null) => {
  const [countryCode = "", langCode = ""] = combined_code.split("-");
  return {
    value: combined_code,
    label: combined_code,
    id,
    countryCode,
    langCode,
  };
};

const buildSectionOptions = (options = []) =>
  options.map((opt, index) => ({
    id: opt.id || index + 1,
    text: opt.text || opt.text_en || "",
    text_translated: opt.text_translated || opt.text_dk || "",
  }));

const buildSectionCountOptions = (sourceOptions = [], savedOptions = []) =>
  sourceOptions.map((opt, index) => {
    const savedOption = savedOptions[index] || {};
    return {
      id: savedOption.id || opt.id || index + 1,
      text: savedOption.text || savedOption.text_en || "",
      text_translated: savedOption.text_translated || savedOption.text_dk || "",
    };
  });

const buildBlankSectionOptions = (options = []) =>
  options.map((opt, index) => ({
    id: opt.id || index + 1,
    text: "",
    text_translated: "",
  }));

const DEFAULT_SECTION = (options = []) => ({
  id: Date.now() + Math.random(),
  translatedQuestionId: null,
  country_language_id: "",
  combinedCode: "",
  question: "",
  question_translated: "",
  options: buildBlankSectionOptions(options),
  showTranslated: false,
  collapsed: true,
});

const UNKNOWN_PREFIX = "__unknown_country_language__";

const AC_TYPE_OPTIONS = [
  "Red - First Aid",
  "Orange - Emergency",
  "Yellow - Cyber",
  "Blue - Machinery",
];

const buildDropdownOptions = (items = []) =>
  [...new Set(items.map((item) => `${item}`.trim()).filter(Boolean))]
    .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }))
    .map((item) => ({ label: item, value: item }));

const ensureOption = (options, value) => {
  const v = `${value || ""}`.trim();
  if (!v) return options;
  if (options.some((o) => o.value === v)) return options;
  return [...options, { label: v, value: v }];
};

const sectionCardStyle = {
  background: "#fff",
  border: "1px solid #e2e8f0",
  borderRadius: "10px",
  marginBottom: "20px",
  boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
  overflow: "hidden",
};

const sectionHeaderStyle = {
  background: "#f8fafc",
  borderBottom: "1px solid #e2e8f0",
  padding: "12px 16px",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
};

const badgeStyle = (color = "#3b82f6") => ({
  display: "inline-block",
  background: color + "18",
  color: color,
  border: `1px solid ${color}40`,
  borderRadius: "20px",
  padding: "2px 10px",
  fontSize: "12px",
  fontWeight: 600,
  letterSpacing: "0.5px",
});

const fieldGroupStyle = {
  background: "#f8fafc",
  border: "1px solid #e9ecef",
  borderRadius: "8px",
  padding: "12px 14px",
  marginBottom: "12px",
};

const formPanelStyle = {
  background: "#fff",
  border: "1px solid #eef2f7",
  borderRadius: "10px",
  boxShadow: "0 8px 24px rgba(15, 23, 42, 0.06)",
  padding: "18px",
  marginBottom: "18px",
};

const containedButtonSx = {
  backgroundColor: THEME_COLOR,
  color: "#fff",
  borderColor: THEME_COLOR,
  textTransform: "none",
  fontWeight: 600,
  "&:hover": {
    backgroundColor: "#e44e55",
    borderColor: "#e44e55",
  },
  "&.Mui-disabled": {
    backgroundColor: "#f7a7ab",
    color: "#fff",
  },
};

const outlinedButtonSx = {
  color: THEME_COLOR,
  borderColor: THEME_COLOR,
  textTransform: "none",
  fontWeight: 600,
  "&:hover": {
    borderColor: "#e44e55",
    backgroundColor: "#fff1f2",
  },
};

const addButtonSx = {
  ...containedButtonSx,
  minHeight: 42,
  minWidth: 160,
  px: 3,
  py: 1,
  fontSize: "0.95rem",
  "& .MuiButton-startIcon": {
    color: "#fff",
  },
};

const QuestionAddUpdate = () => {
  const history = useHistory();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const questionId = params.get("id");

  const dispatch = useDispatch();
  const { selectedQuestion, questionTags } = useSelector(
    (state) => state.questions,
  );
  const { dropdownList = [] } = useSelector((state) => state.countryLanguage);
  const combinedCodeOptions = useMemo(
    () =>
      (dropdownList || []).map((item) =>
        parseCombinedCode(
          item.combined_code,
          item.id || item.country_language_id || item.countryLanguageId,
        ),
      ),
    [dropdownList],
  );

  const today = new Date().toISOString().split("T")[0];

  const [translatingSectionId, setTranslatingSectionId] = useState(null);
  const sectionsTranslateLoading = translatingSectionId !== null;

  const [formData, setFormData] = useState({
    date: today,
    version: "1.0",
    active: false,
    question_en: "",
    letter: "",
    tag: "",
    options: [
      { text_en: "", correct: false },
      { text_en: "", correct: false },
    ],
  });

  const [errors, setErrors] = useState({});
  const [tagOptions, setTagOptions] = useState([]);
  const [letterOptions, setLetterOptions] = useState(
    buildDropdownOptions(AC_TYPE_OPTIONS),
  );
  const [countrySections, setCountrySections] = useState([]);
  const [sectionErrors, setSectionErrors] = useState({});

  const getCountryLanguageId = (section) =>
    section.country_language_id ||
    combinedCodeOptions.find((opt) => opt.value === section.combinedCode)?.id ||
    "";

  const usedCountryLanguageIds = countrySections
    .map((section) => `${getCountryLanguageId(section)}`)
    .filter(Boolean);
  const allCountryLanguagesUsed =
    combinedCodeOptions.length > 0 &&
    countrySections.length >= combinedCodeOptions.length;
  const defaultCountryLanguageOption = useMemo(
    () => findDefaultCountryLanguageOption(combinedCodeOptions),
    [combinedCodeOptions],
  );
  const defaultCountrySection = useMemo(
    () =>
      countrySections.find((section) =>
        isDefaultCountryLanguageSection(section, combinedCodeOptions),
      ) || null,
    [combinedCodeOptions, countrySections],
  );
  const defaultQuestionText =
    defaultCountrySection?.question || formData.question_en;
  const getDefaultOptionText = (index) =>
    defaultCountrySection?.options?.[index]?.text ||
    formData.options[index]?.text_en ||
    "";

  const getSectionSelectOptions = (currentSection) => {
    const selectedIds = countrySections
      .filter((section) => section.id !== currentSection.id)
      .map((section) => `${getCountryLanguageId(section)}`)
      .filter(Boolean);
    const options = combinedCodeOptions.filter(
      (option) => !selectedIds.includes(`${option.id}`),
    );
    if (
      currentSection.country_language_id &&
      !combinedCodeOptions.some(
        (option) => `${option.id}` === `${currentSection.country_language_id}`,
      )
    ) {
      return [
        {
          value: `${UNKNOWN_PREFIX}${currentSection.country_language_id}`,
          label: `Unknown (${currentSection.country_language_id})`,
          id: currentSection.country_language_id,
          countryCode: "Unknown",
          langCode: "",
        },
        ...options,
      ];
    }
    return options;
  };

  const getSectionSelectValue = (section) => {
    if (
      section.country_language_id &&
      !combinedCodeOptions.some(
        (option) => `${option.id}` === `${section.country_language_id}`,
      )
    ) {
      return `${UNKNOWN_PREFIX}${section.country_language_id}`;
    }
    return section.combinedCode;
  };

  const getSectionLabel = (section) => {
    if (
      section.country_language_id &&
      !combinedCodeOptions.some(
        (option) => `${option.id}` === `${section.country_language_id}`,
      )
    ) {
      return {
        countryCode: "Unknown",
        langCode: `${section.country_language_id}`,
      };
    }
    return section.combinedCode
      ? parseCombinedCode(section.combinedCode)
      : null;
  };
  useEffect(() => {
    dispatch(fetchQuestionTags());
    dispatch(fetchCombinedCode());
  }, [dispatch]);

  useEffect(() => {
    if (!defaultCountryLanguageOption) return;

    setCountrySections((prev) => {
      if (
        prev.some((section) =>
          isDefaultCountryLanguageSection(section, combinedCodeOptions),
        )
      ) {
        return prev;
      }

      return [
        {
          ...DEFAULT_SECTION(formData.options),
          country_language_id: defaultCountryLanguageOption.id || "",
          combinedCode: defaultCountryLanguageOption.value,
          question: formData.question_en,
          options: formData.options.map((opt, index) => ({
            id: opt.id || index + 1,
            text: opt.text_en || "",
            text_translated: "",
          })),
          collapsed: false,
        },
        ...prev,
      ];
    });
  }, [
    combinedCodeOptions,
    countrySections,
    defaultCountryLanguageOption,
    formData.options,
    formData.question_en,
  ]);

  useEffect(() => {
    setTagOptions((prev) =>
      ensureOption(
        buildDropdownOptions([
          ...(questionTags || []),
          ...prev.map((i) => i.value),
        ]),
        formData.tag,
      ),
    );
  }, [questionTags, formData.tag]);

  useEffect(() => {
    if (questionId) dispatch(fetchQuestionById(questionId));
    return () => dispatch(clearSelectedQuestion());
  }, [dispatch, questionId]);

  useEffect(() => {
    if (selectedQuestion) {
      setFormData({
        date: selectedQuestion.date_added || today,
        version: selectedQuestion.version || "1.0",
        active: selectedQuestion.active || false,
        question_en: selectedQuestion.question || "",
        options: (selectedQuestion.options || []).map((opt, index) => ({
          id: opt.id || index + 1,
          text_en: opt.text_en || opt.text || "",
          correct: Boolean(opt.correct),
        })),
        tag: selectedQuestion.tag || "",
        letter: selectedQuestion.letter || "",
      });
      setTagOptions((prev) => ensureOption(prev, selectedQuestion.tag));
      setLetterOptions((prev) => ensureOption(prev, selectedQuestion.letter));
      const savedSections =
        selectedQuestion.translations ||
        selectedQuestion.translated_questions ||
        selectedQuestion.country_sections ||
        [];
      if (savedSections.length) {
        setCountrySections(
          savedSections.map((s) => {
            const option = combinedCodeOptions.find(
              (item) =>
                `${item.id}` === `${s.country_language_id}` ||
                item.value === s.combinedCode ||
                item.value === s.combined_code,
            );
            return {
              ...DEFAULT_SECTION(selectedQuestion.options || []),
              ...s,
              id: `translation_${s.id || Date.now() + Math.random()}`,
              translatedQuestionId: s.id || s.translatedQuestionId || null,
              country_language_id: s.country_language_id || option?.id || "",
              combinedCode:
                s.combinedCode || s.combined_code || option?.value || "",
              options: buildSectionCountOptions(
                selectedQuestion.options || [],
                s.options || [],
              ),
              showTranslated: Boolean(
                s.question_translated ||
                  s.options?.some((opt) => opt.text_translated),
              ),
              collapsed: true,
            };
          }),
        );
      } else {
        setCountrySections((prev) =>
          prev.map((section) => ({
            ...section,
            options: buildSectionOptions(selectedQuestion.options || []),
            showTranslated: Boolean(
              section.question_translated ||
                section.options?.some((opt) => opt.text_translated),
            ),
            collapsed: true,
          })),
        );
      }
    }
  }, [combinedCodeOptions, selectedQuestion, today]);
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleCreatableSelectChange = (name, selectedOption) => {
    setFormData((prev) => ({ ...prev, [name]: selectedOption?.value || "" }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleCreateSelectOption = (name, setter, inputValue) => {
    const v = inputValue.trim();
    if (!v) return;
    setter((prev) => ensureOption(prev, v));
    setFormData((prev) => ({ ...prev, [name]: v }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleOptionChange = (index, field, value) => {
    const updated = [...formData.options];
    updated[index][field] = value;
    setFormData({ ...formData, options: updated });
  };

  const handleCorrectAnswer = (index) => {
    const updated = formData.options.map((opt, i) =>
      i === index ? { ...opt, correct: !opt.correct } : opt,
    );
    setFormData((prev) => ({ ...prev, options: updated }));
    setErrors((prev) => ({ ...prev, correct: "" }));
  };

  const addOption = () => {
    const newOption = { text_en: "", correct: false };
    setFormData((prev) => ({ ...prev, options: [...prev.options, newOption] }));
    setCountrySections((prev) =>
      prev.map((section) => ({
        ...section,
        options: [
          ...section.options,
          { id: section.options.length + 1, text: "", text_translated: "" },
        ],
      })),
    );
  };

  const removeOption = (index) => {
    setFormData((prev) => ({
      ...prev,
      options: prev.options.filter((_, i) => i !== index),
    }));
    setCountrySections((prev) =>
      prev.map((section) => ({
        ...section,
        options: section.options.filter((_, i) => i !== index),
      })),
    );
  };
  const addCountrySection = () => {
    if (allCountryLanguagesUsed) return;
    setCountrySections((prev) => [...prev, DEFAULT_SECTION(formData.options)]);
  };

  const removeCountrySection = async (section) => {
    if (isDefaultCountryLanguageSection(section, combinedCodeOptions)) return;

    if (questionId && section.translatedQuestionId) {
      try {
        await dispatch(
          removeTranslatedQuestion(section.translatedQuestionId),
        ).unwrap();
        toast.success("Translation removed successfully");
      } catch {
        toast.error("Translation remove failed");
        return;
      }
    }

    setCountrySections((prev) => prev.filter((s) => s.id !== section.id));
  };

  const toggleCollapse = (id) =>
    setCountrySections((prev) =>
      prev.map((s) => (s.id === id ? { ...s, collapsed: !s.collapsed } : s)),
    );

  const updateSection = (id, field, value) => {
    setCountrySections((prev) =>
      prev.map((s) => {
        if (s.id !== id) return s;
        if (field !== "combinedCode") return { ...s, [field]: value };
        if (isDefaultCountryLanguageSection(s, combinedCodeOptions)) return s;
        if (value.startsWith(UNKNOWN_PREFIX)) return s;
        const selectedOption = combinedCodeOptions.find(
          (opt) => opt.value === value,
        );
        return {
          ...s,
          combinedCode: value,
          country_language_id: selectedOption?.id || "",
        };
      }),
    );
    setSectionErrors((prev) => {
      const next = { ...prev };
      delete next[`${id}_${field}`];
      return next;
    });
  };

  const updateSectionOption = (id, optIndex, field, value) => {
    setCountrySections((prev) =>
      prev.map((s) => {
        if (s.id !== id) return s;
        const opts = [...s.options];
        opts[optIndex] = { ...opts[optIndex], [field]: value };
        return { ...s, options: opts };
      }),
    );
    setSectionErrors((prev) => {
      const next = { ...prev };
      delete next[`${id}_opt_${optIndex}`];
      return next;
    });
  };
  const handleTranslateSections = async (sectionId) => {
    const targetSections = sectionId
      ? countrySections.filter((section) => section.id === sectionId)
      : countrySections;
    const selectedCountryIds = targetSections
      .map((section) => Number(getCountryLanguageId(section)))
      .filter(Boolean);

    if (!formData.question_en.trim()) {
      toast.warning("Add English question to translate");
      return;
    }

    if (!selectedCountryIds.length) {
      toast.warning(
        "Select at least one country-language section to translate",
      );
      return;
    }

    try {
      setTranslatingSectionId(sectionId || "all");

      const payload = {
        question: formData.question_en,
        options: formData.options.map((opt, i) => ({
          id: i + 1,
          text: opt.text_en,
        })),
        selected_country_ids: selectedCountryIds,
      };

      const resultAction = await dispatch(translateQuestion(payload));
      if (!translateQuestion.fulfilled.match(resultAction)) throw new Error();

      const translations = Array.isArray(resultAction.payload)
        ? resultAction.payload
        : resultAction.payload?.data || [];

      setCountrySections((prev) =>
        prev.map((section) => {
          if (sectionId && section.id !== sectionId) return section;
          const translatedSection = translations.find(
            (item) =>
              `${item.selected_country}` === `${getCountryLanguageId(section)}`,
          );
          if (!translatedSection) return section;

          return {
            ...section,
            question: translatedSection.question || section.question,
            options: buildSectionCountOptions(
              formData.options,
              translatedSection.options || [],
            ),
            showTranslated: false,
          };
        }),
      );
      toast.success("Section translations applied");
    } catch {
      toast.error("Section translation failed");
    } finally {
      setTranslatingSectionId(null);
    }
  };
  const validate = () => {
    let newErrors = {};
    const hasDefaultSection = countrySections.some((section) =>
      isDefaultCountryLanguageSection(section, combinedCodeOptions),
    );

    if (!defaultCountryLanguageOption || !hasDefaultSection) {
      newErrors.default_country_language = `${DEFAULT_COUNTRY_LANGUAGE_CODE} country/language section is required`;
    }

    if (!defaultQuestionText.trim())
      newErrors.question_en = "English question is required";
    formData.options.forEach((opt, index) => {
      if (!getDefaultOptionText(index).trim())
        newErrors[`option_en_${index}`] = "Option text is required";
    });
    if (!formData.options.some((o) => o.correct))
      newErrors.correct = "At least one correct answer is required";

    let sectionErrs = {};
    countrySections.forEach((s) => {
      if (!getCountryLanguageId(s))
        sectionErrs[`${s.id}_combinedCode`] = "Please select a language";
      if (!s.question.trim())
        sectionErrs[`${s.id}_question`] = "Question is required";
      s.options.forEach((opt, i) => {
        if (!opt.text.trim())
          sectionErrs[`${s.id}_opt_${i}`] = "Option text is required";
      });
    });

    setErrors(newErrors);
    setSectionErrors(sectionErrs);
    return (
      Object.keys(newErrors).length === 0 &&
      Object.keys(sectionErrs).length === 0
    );
  };
  const handleSubmit = async () => {
    if (!validate()) return;
    const correctCount = formData.options.filter((o) => o.correct).length;
    try {
      const payload = {
        question: defaultQuestionText,
        tag: formData.tag,
        letter: formData.letter,
        active: formData.active,
        version: formData.version,
        correct_answers: correctCount,
        options: formData.options.map((opt, i) => ({
          id: i + 1,
          text: getDefaultOptionText(i),
          correct: opt.correct,
        })),
        translated_questions: countrySections.map((s) => ({
          ...(questionId && s.translatedQuestionId
            ? { id: s.translatedQuestionId }
            : {}),
          country_language_id: Number(getCountryLanguageId(s)),
          question: s.question,
          options: s.options.map((o, i) => ({
            id: i + 1,
            text: o.text,
          })),
        })),
      };
      console.log(
        `[QuestionAddUpdate] ${questionId ? "Update" : "Add"} question payload`,
        payload,
      );

      if (questionId) {
        await dispatch(
          updateQuestion({ id: questionId, data: payload }),
        ).unwrap();
        toast.success("Question updated successfully");
      } else {
        await dispatch(addQuestion(payload)).unwrap();
        toast.success("Question added successfully");
      }
      history.push("/admin-question");
    } catch {
      toast.error("Save failed");
    }
  };
  return (
    <div className="container-fluid mt-3 pb-5 overflow-auto">
      <FormPageHeader
        title={questionId ? "Update Question" : "Add Question"}
        onBack={() => history.push("/admin-question")}
      />

      <div style={formPanelStyle}>
        <div className="row g-3 g-md-4">
          <div className="col-12 col-md-6 col-lg-4">
            <label className="form-label fw-semibold">Date</label>
            <input
              type="date"
              className="form-control"
              value={formData.date}
              disabled
            />
          </div>
          <div className="col-12 col-md-6 col-lg-4">
            <label className="form-label fw-semibold">Version</label>
            <input
              type="text"
              name="version"
              className="form-control"
              value={formData.version}
              disabled
            />
          </div>
          <div className="col-12 col-md-6 col-lg-4">
            <label className="form-label fw-semibold">Active</label>

            <div
              className="d-flex align-items-center"
              style={{ height: "38px" }}
            >
              <div className="form-check form-switch m-0">
                <input
                  type="checkbox"
                  className="form-check-input"
                  name="active"
                  checked={formData.active}
                  onChange={handleChange}
                  style={{
                    width: "2.8em",
                    height: "1.5em",
                    cursor: "pointer",
                    marginLeft: 0,
                  }}
                />
              </div>
            </div>
          </div>
          <div className="col-12 col-md-6 col-lg-4">
            <label className="form-label fw-semibold">AC Type</label>
            <CreatableSelect
              isClearable
              options={letterOptions}
              value={
                formData.letter
                  ? { label: formData.letter, value: formData.letter }
                  : null
              }
              onChange={(opt) => handleCreatableSelectChange("letter", opt)}
              onCreateOption={(v) =>
                handleCreateSelectOption("letter", setLetterOptions, v)
              }
              placeholder="Select..."
            />
          </div>
          <div className="col-12 col-md-6 col-lg-4">
            <label className="form-label fw-semibold">Tag</label>
            <CreatableSelect
              isClearable
              options={tagOptions}
              value={
                formData.tag
                  ? { label: formData.tag, value: formData.tag }
                  : null
              }
              onChange={(opt) => handleCreatableSelectChange("tag", opt)}
              onCreateOption={(v) =>
                handleCreateSelectOption("tag", setTagOptions, v)
              }
              placeholder="Select..."
            />
          </div>
        </div>
      </div>
      <div style={formPanelStyle}>
        <label className="form-label fw-semibold">
          English Question <span className="text-danger">*</span>
        </label>
        <textarea
          rows="3"
          name="question_en"
          value={defaultQuestionText}
          readOnly
          className={`form-control ${errors.question_en ? "is-invalid" : ""}`}
          placeholder={`${DEFAULT_COUNTRY_LANGUAGE_CODE} question is shown here`}
        />
        {errors.question_en && (
          <div className="invalid-feedback">{errors.question_en}</div>
        )}
        <div className="d-flex justify-content-between align-items-center mt-4 mb-3 flex-wrap gap-2">
          <h6 className="mb-0 fw-semibold">
            Answer Options <span className="text-danger">*</span>
          </h6>
          <Button
            variant="contained"
            size="medium"
            startIcon={<FiPlus />}
            sx={addButtonSx}
            onClick={addOption}
          >
            Add Option
          </Button>
        </div>
        <div className="row fw-semibold text-muted small mb-2 px-1 d-none d-sm-flex">
          <div className="col-sm-2 col-md-1 text-center">Correct</div>
          <div className="col-sm-8 col-md-9">English Option</div>
          <div className="col-sm-2 col-md-1"></div>
        </div>

        {formData.options.map((opt, index) => (
          <div
            className="row mb-2 align-items-start px-1"
            key={index}
            style={{
              background: "transparent",
              borderRadius: "6px",
              padding: "4px 0",
            }}
          >
            <div className="col-2 col-sm-2 col-md-1 text-center pt-2">
              <input
                type="checkbox"
                className="form-check-input"
                style={{ width: "18px", height: "18px", cursor: "pointer" }}
                checked={opt.correct}
                onChange={() => handleCorrectAnswer(index)}
                title="Mark as correct answer"
              />
            </div>

            <div className="col-8 col-sm-8 col-md-9">
              <input
                className={`form-control ${
                  errors[`option_en_${index}`] ? "is-invalid" : ""
                }`}
                value={getDefaultOptionText(index)}
                readOnly
                placeholder={`Option ${index + 1}`}
              />
              {errors[`option_en_${index}`] && (
                <div className="invalid-feedback">
                  {errors[`option_en_${index}`]}
                </div>
              )}
            </div>

            <div className="col-2 col-sm-2 col-md-1 text-end pt-1">
              {formData.options.length > 2 && (
                <Button
                  variant="outlined"
                  size="small"
                  sx={{ ...outlinedButtonSx, minWidth: 34, padding: "4px 6px" }}
                  onClick={() => removeOption(index)}
                  title="Remove option"
                >
                  <FiTrash2 />
                </Button>
              )}
            </div>
          </div>
        ))}

        {errors.correct && (
          <div
            className="alert alert-warning py-2 mt-2"
            style={{ fontSize: "13px" }}
          >
            {errors.correct}
          </div>
        )}
      </div>

      <div style={formPanelStyle}>
        <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
          <div>
            <h5 className="mb-0 fw-semibold">
              Additional Country / Language Sections
            </h5>
            <small className="text-muted">
              Add translated question text and matching option text for each
              language. The {DEFAULT_COUNTRY_LANGUAGE_CODE} section is required
              and provides the header English question.
            </small>
          </div>
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <Button
              variant="contained"
              size="medium"
              startIcon={<FiPlus />}
              sx={addButtonSx}
              onClick={addCountrySection}
              disabled={allCountryLanguagesUsed}
            >
              Add Section
            </Button>
          </div>
        </div>

        {errors.default_country_language && (
          <div className="text-danger small mb-3">
            {errors.default_country_language}
          </div>
        )}

        {countrySections.length === 0 && (
          <div
            className="text-center text-muted py-4 mb-3"
            style={{
              border: "2px dashed #e2e8f0",
              borderRadius: "10px",
              fontSize: "14px",
            }}
          >
            <FiGlobe
              style={{ fontSize: "28px", marginBottom: "6px", opacity: 0.4 }}
            />
            <div>
              No additional sections yet. Click <strong>+ Add Section</strong>{" "}
              to add one.
            </div>
          </div>
        )}

        {countrySections.map((section, sIdx) => {
          const parsed = getSectionLabel(section);
          const hasError = Object.keys(sectionErrors).some((k) =>
            k.startsWith(`${section.id}_`),
          );
          const sectionSelectOptions = getSectionSelectOptions(section);

          return (
            <div key={section.id} style={sectionCardStyle}>
              <div style={sectionHeaderStyle}>
                <div className="d-flex align-items-center gap-2 flex-wrap">
                  <span className="fw-semibold" style={{ fontSize: "14px" }}>
                    Section {sIdx + 1}
                  </span>
                  {parsed && (
                    <span style={badgeStyle("#3b82f6")}>
                      {parsed.countryCode} · {parsed.langCode}
                    </span>
                  )}
                  {hasError && (
                    <span style={badgeStyle(THEME_COLOR)}>Incomplete</span>
                  )}
                </div>
                <div className="d-flex align-items-center gap-2">
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={
                      translatingSectionId === section.id ? undefined : (
                        <FiGlobe />
                      )
                    }
                    sx={outlinedButtonSx}
                    onClick={() => handleTranslateSections(section.id)}
                    disabled={
                      sectionsTranslateLoading || !getCountryLanguageId(section)
                    }
                  >
                    {translatingSectionId === section.id ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" />
                        Translating…
                      </>
                    ) : (
                      "Translate"
                    )}
                  </Button>
                  <Button
                    size="small"
                    sx={{
                      minWidth: 32,
                      padding: "4px",
                      color: THEME_COLOR,
                      border: 0,
                      background: "transparent",
                      "&:hover": { background: "#fff1f2" },
                    }}
                    onClick={() => toggleCollapse(section.id)}
                    title={section.collapsed ? "Expand" : "Collapse"}
                  >
                    {section.collapsed ? (
                      <FiChevronDown size={20} />
                    ) : (
                      <FiChevronUp size={20} />
                    )}
                  </Button>
                  <Button
                    size="small"
                    sx={outlinedButtonSx}
                    onClick={() => removeCountrySection(section)}
                    disabled={isDefaultCountryLanguageSection(
                      section,
                      combinedCodeOptions,
                    )}
                  >
                    <MdDeleteOutline size={22}/>
                  </Button>
                </div>
              </div>
              {!section.collapsed && (
                <div className="p-3">
                  <div className="row mb-3 align-items-end g-3">
                    <div className="col-md-4">
                      <label
                        className="form-label fw-semibold"
                        style={{ fontSize: "13px" }}
                      >
                        Country · Language{" "}
                        <span className="text-danger">*</span>
                      </label>
                      <select
                        className={`form-select ${
                          sectionErrors[`${section.id}_combinedCode`]
                            ? "is-invalid"
                            : ""
                        }`}
                        value={getSectionSelectValue(section)}
                        disabled={isDefaultCountryLanguageSection(
                          section,
                          combinedCodeOptions,
                        )}
                        onChange={(e) =>
                          updateSection(
                            section.id,
                            "combinedCode",
                            e.target.value,
                          )
                        }
                      >
                        <option value="">Select country-language…</option>
                        {sectionSelectOptions.map((opt) => (
                          <option
                            key={opt.value}
                            value={opt.value}
                            disabled={`${opt.value}`.startsWith(UNKNOWN_PREFIX)}
                          >
                            {opt.label}
                          </option>
                        ))}
                      </select>
                      {sectionErrors[`${section.id}_combinedCode`] && (
                        <div className="invalid-feedback">
                          {sectionErrors[`${section.id}_combinedCode`]}
                        </div>
                      )}
                    </div>
                  </div>
                  <div style={fieldGroupStyle}>
                    <label
                      className="form-label fw-semibold mb-1"
                      style={{ fontSize: "13px" }}
                    >
                      Question <span className="text-danger">*</span>
                      {parsed && (
                        <span className="text-muted fw-normal ms-1">
                          ({parsed.countryCode} · {parsed.langCode})
                        </span>
                      )}
                    </label>
                    <textarea
                      rows="3"
                      className={`form-control ${
                        sectionErrors[`${section.id}_question`]
                          ? "is-invalid"
                          : ""
                      }`}
                      value={section.question}
                      onChange={(e) =>
                        updateSection(section.id, "question", e.target.value)
                      }
                      placeholder="Enter the question text…"
                    />
                    {sectionErrors[`${section.id}_question`] && (
                      <div className="invalid-feedback">
                        {sectionErrors[`${section.id}_question`]}
                      </div>
                    )}
                  </div>
                  {section.showTranslated && (
                    <div style={fieldGroupStyle}>
                      <label
                        className="form-label fw-semibold mb-1"
                        style={{ fontSize: "13px" }}
                      >
                        Translated Question
                      </label>
                      <textarea
                        rows="3"
                        className="form-control"
                        value={section.question_translated}
                        onChange={(e) =>
                          updateSection(
                            section.id,
                            "question_translated",
                            e.target.value,
                          )
                        }
                        placeholder="Translated question…"
                      />
                    </div>
                  )}
                  <div className="d-flex justify-content-between align-items-center mb-2 mt-2">
                    <label
                      className="fw-semibold mb-0"
                      style={{ fontSize: "13px" }}
                    >
                      Answer Options <span className="text-danger">*</span>
                    </label>
                  </div>
                  <div
                    className="row fw-semibold text-muted mb-1 px-1"
                    style={{ fontSize: "12px" }}
                  >
                    <div className="col-12">Option Text</div>
                  </div>

                  {section.options.map((opt, oIdx) => (
                    <div key={oIdx} className="mb-2">
                      <input
                        className={`form-control form-control-sm ${
                          sectionErrors[`${section.id}_opt_${oIdx}`]
                            ? "is-invalid"
                            : ""
                        }`}
                        value={opt.text}
                        placeholder={`Option ${oIdx + 1}`}
                        onChange={(e) =>
                          updateSectionOption(
                            section.id,
                            oIdx,
                            "text",
                            e.target.value,
                          )
                        }
                      />
                      {sectionErrors[`${section.id}_opt_${oIdx}`] && (
                        <div
                          className="invalid-feedback"
                          style={{ fontSize: "11px" }}
                        >
                          {sectionErrors[`${section.id}_opt_${oIdx}`]}
                        </div>
                      )}
                    </div>
                  ))}

                  {section.showTranslated && (
                    <div
                      className="mt-3 pt-2 text-success"
                      style={{
                        borderTop: "1px solid #f1f5f9",
                        fontSize: "12px",
                      }}
                    >
                      Translation applied
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div
        className="d-flex flex-column flex-sm-row justify-content-end align-items-stretch align-items-sm-center gap-3 mt-4 pt-3"
        style={{ borderTop: `2px solid ${THEME_COLOR}` }}
      >
        <Button
          variant="outlined"
          sx={outlinedButtonSx}
          onClick={() => history.push("/admin-question")}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          sx={{ ...containedButtonSx, minWidth: "160px", height: "42px" }}
          onClick={handleSubmit}
          disabled={sectionsTranslateLoading}
        >
          {questionId ? "Update Question" : "Add Question"}
        </Button>
      </div>
    </div>
  );
};

export default QuestionAddUpdate;
